/**
 * @fileoverview Cookie consent state utilities for COGSLOG.
 *
 * Provides the single consumption interface for reading and writing cookie
 * consent preferences. Any tracking script can call getConsentState() to
 * determine current consent before firing (LEGAL-09).
 *
 * Preferences are stored in localStorage under COOKIE_PREFS_KEY.
 * Necessary cookies are always forced to true regardless of stored value.
 */

/**
 * localStorage key for cookie consent preferences.
 * @constant {string}
 */
export const COOKIE_PREFS_KEY = 'cogslog_cookie_prefs';

/**
 * Default consent state used when no preferences have been saved.
 * Deny-all by default (except necessary which is always on).
 *
 * @constant {{necessary: boolean, analytics: boolean, marketing: boolean}}
 */
const DEFAULT_CONSENT = {
  necessary: true,
  analytics: false,
  marketing: false,
};

/**
 * Read the current cookie consent state from localStorage.
 *
 * Returns the stored preferences if available, or the deny-all defaults
 * if no preferences have been saved. Always forces necessary: true
 * regardless of what is stored.
 *
 * Any tracking script should call this before firing to check whether
 * analytics or marketing consent has been granted.
 *
 * @returns {{necessary: boolean, analytics: boolean, marketing: boolean}}
 *   Current consent state. necessary is always true.
 *
 * @example
 * const { analytics } = getConsentState();
 * if (analytics) {
 *   // fire analytics script
 * }
 */
export function getConsentState() {
  try {
    const raw = localStorage.getItem(COOKIE_PREFS_KEY);
    if (!raw) {
      return { ...DEFAULT_CONSENT };
    }

    const parsed = JSON.parse(raw);
    return {
      necessary: true, // always forced true
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
    };
  } catch {
    // Malformed JSON or localStorage unavailable — return safe defaults
    return { ...DEFAULT_CONSENT };
  }
}

/**
 * Write cookie consent preferences to localStorage.
 *
 * Always forces necessary: true regardless of input. Adds a timestamp
 * to the stored value for audit purposes.
 *
 * Called by CookieBanner when the user clicks Accept All, Reject All,
 * or saves preferences via the CookiePreferencesModal.
 *
 * @param {{necessary?: boolean, analytics: boolean, marketing: boolean}} prefs
 *   Preferences to save. necessary is always forced to true.
 * @returns {void}
 *
 * @example
 * setConsentState({ necessary: true, analytics: true, marketing: false });
 */
export function setConsentState(prefs) {
  const toStore = {
    necessary: true, // always forced true
    analytics: Boolean(prefs.analytics),
    marketing: Boolean(prefs.marketing),
    timestamp: new Date().toISOString(),
  };

  try {
    localStorage.setItem(COOKIE_PREFS_KEY, JSON.stringify(toStore));
  } catch {
    // localStorage unavailable (e.g., private browsing with strict settings)
    // Fail silently — banner will re-appear next visit
  }
}
